import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";

import type { SnapPageArtifact } from "@/features/snapping/types";

/** Loads a page artifact; revisionId takes precedence over runId when both exist. */
export function usePageSnapArtifact(
  enabled: boolean,
  runId: Id<"documentIntelligenceRuns"> | null | undefined,
  pageNumber: number,
  revisionId?: Id<"projectDocumentRevisions"> | string | null
) {
  const shouldQueryRevision = enabled && !!revisionId;
  const shouldQueryRun = enabled && !revisionId && !!runId;
  const {
    data: latestRevisionArtifactRecord,
    error: latestRevisionRecordError,
    isPending: isLatestRevisionRecordPending,
  } = useQuery(
    convexQuery(
      api.documentIntelligence.snapArtifacts.getLatestPageArtifactByRevision,
      shouldQueryRevision
        ? {
            revisionId: revisionId as Id<"projectDocumentRevisions">,
            pageNumber,
          }
        : "skip"
    )
  );
  const {
    data: runArtifactRecord,
    error: runRecordError,
    isPending: isRunRecordPending,
  } = useQuery(
    convexQuery(
      api.documentIntelligence.snapArtifacts.getPageArtifact,
      shouldQueryRun
        ? {
            runId,
            pageNumber,
          }
        : "skip"
    )
  );

  const artifactRecord = latestRevisionArtifactRecord ?? runArtifactRecord;
  const fileUrl = artifactRecord?.fileUrl ?? null;
  const shouldQueryArtifact = enabled && !!fileUrl;

  const artifactQuery = useQuery({
    queryKey: [
      "document-intelligence-snap-artifact",
      artifactRecord?._id ?? null,
      artifactRecord?.runId ?? null,
      artifactRecord?.updatedAt ?? null,
      artifactRecord?.byteSize ?? null,
      fileUrl,
    ],
    enabled: shouldQueryArtifact,
    staleTime: Infinity,
    retry: false,
    queryFn: async (): Promise<SnapPageArtifact | null> => {
      if (!fileUrl) {
        return null;
      }
      const response = await fetch(fileUrl, { cache: "no-store" });
      if (!response.ok) {
        throw new Error(`Failed to load snap artifact (${response.status})`);
      }
      return (await response.json()) as SnapPageArtifact;
    },
  });

  return {
    artifact: artifactQuery.data ?? null,
    artifactRecord: artifactRecord ?? null,
    error:
      latestRevisionRecordError ?? runRecordError ?? artifactQuery.error,
    isLoading:
      (shouldQueryRevision && isLatestRevisionRecordPending) ||
      (shouldQueryRun && isRunRecordPending) ||
      (shouldQueryArtifact && artifactQuery.isPending),
  };
}
